// ============================================================
// OAMATECH ERP PRO - Componentes UI reutilizables
// ============================================================
import React from 'react';
import { Loader2, X, Euro, DollarSign } from 'lucide-react';
import { formatoMoneda, esEspana } from '../../utils/instancia';
import clsx from 'clsx';

// ── Badge de estado de reparación ────────────────────────────
const ESTADOS_CONFIG = {
  recibido:        { label: 'Recibido',         color: 'bg-gray-100 text-gray-700' },
  diagnostico:     { label: 'Diagnóstico',      color: 'bg-yellow-100 text-yellow-700' },
  esperando_pieza: { label: 'Esp. pieza',       color: 'bg-orange-100 text-orange-700' },
  reparando:       { label: 'Reparando',        color: 'bg-blue-100 text-blue-700' },
  listo:           { label: '✓ Listo',          color: 'bg-green-100 text-green-700' },
  entregado:       { label: 'Entregado',        color: 'bg-gray-100 text-gray-500' },
  cancelado:       { label: 'Cancelado',        color: 'bg-red-100 text-red-600' },
};

export function BadgeEstado({ estado }) {
  const cfg = ESTADOS_CONFIG[estado] || { label: estado, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', cfg.color)}>
      {cfg.label}
    </span>
  );
}

export { ESTADOS_CONFIG };

// ── Spinner de carga ─────────────────────────────────────────
export function Spinner({ size = 20, className = '' }) {
  return <Loader2 size={size} className={clsx('animate-spin text-blue-600', className)} />;
}

export function CargandoCentrado() {
  return (
    <div className="flex items-center justify-center h-48">
      <Spinner size={28} />
    </div>
  );
}

// ── Tarjeta estadística del dashboard ────────────────────────
export function StatCard({ titulo, valor, icono: Icono, color = 'blue', subtitulo, tendencia }) {
  const colores = {
    blue:   { bg: 'bg-blue-50',   icon: 'bg-blue-100 text-blue-600',   text: 'text-blue-700' },
    green:  { bg: 'bg-green-50',  icon: 'bg-green-100 text-green-600', text: 'text-green-700' },
    orange: { bg: 'bg-orange-50', icon: 'bg-orange-100 text-orange-600',text: 'text-orange-700' },
    purple: { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-600',text: 'text-purple-700' },
    red:    { bg: 'bg-red-50',    icon: 'bg-red-100 text-red-600',     text: 'text-red-700' },
  };
  const c = colores[color] || colores.blue;

  return (
    <div className={clsx('card flex items-start gap-4', c.bg, 'border-0')}>
      <div className={clsx('w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0', c.icon)}>
        <Icono size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-500 font-medium">{titulo}</p>
        <p className={clsx('text-2xl font-bold mt-0.5', c.text)}>{valor}</p>
        {subtitulo && <p className="text-xs text-gray-400 mt-0.5">{subtitulo}</p>}
      </div>
    </div>
  );
}

// ── Modal reutilizable ────────────────────────────────────────
export function Modal({ abierto, onClose, titulo, children, ancho = 'max-w-lg' }) {
  if (!abierto) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={clsx('relative bg-white rounded-2xl shadow-xl w-full', ancho, 'max-h-[90vh] flex flex-col')}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 text-base">{titulo}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>
        {/* Cuerpo scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Input con label ───────────────────────────────────────────
export function Campo({ label, error, className, required, ...props }) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        className={clsx(
          'input',
          error && 'border-red-300 focus:ring-red-400'
        )}
        {...props}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export function Select({ label, error, className, children, required, ...props }) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        className={clsx(
          'input bg-white',
          error && 'border-red-300'
        )}
        {...props}
      >
        {children}
      </select>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export function Textarea({ label, error, className, required, ...props }) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        rows={3}
        className={clsx('input resize-none', error && 'border-red-300')}
        {...props}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Tabla vacía placeholder ───────────────────────────────────
export function TablaVacia({ mensaje = 'No hay datos', icono = '📋' }) {
  return (
    <div className="text-center py-14 text-gray-400">
      <div className="text-4xl mb-3">{icono}</div>
      <p className="font-medium text-gray-500">{mensaje}</p>
    </div>
  );
}

// ── Formateo de moneda ────────────────────────────────────────
// Icono de la moneda del país (€ o $)
export function IconoMoneda(props) {
  return esEspana() ? <Euro {...props}/> : <DollarSign {...props}/>;
}

// (se sigue llamando formatEuro, pero sale en la moneda del país de la instalación)
export function formatEuro(valor) {
  return formatoMoneda(valor);
}

export function formatFecha(fecha) {
  if (!fecha) return '—';
  // "2026-10-26" es ese día aquí (con new Date() sería medianoche de Londres: en Colombia saldría el 25)
  const s = String(fecha);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) : new Date(fecha);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Filtro de fechas reutilizable (desde/hasta) — se usa en todos los módulos
export function FiltroFechas({ desde, hasta, setDesde, setHasta, className = '' }) {
  return (
    <div className={'flex items-center gap-1 ' + className}>
      <span className="text-xs text-gray-400">De</span>
      <input type="date" className="input text-xs py-1.5" value={desde} onChange={e=>setDesde(e.target.value)}/>
      <span className="text-xs text-gray-400">a</span>
      <input type="date" className="input text-xs py-1.5" value={hasta} onChange={e=>setHasta(e.target.value)}/>
      {(desde||hasta) && <button onClick={()=>{setDesde('');setHasta('');}} className="text-xs text-blue-600 hover:underline ml-1">✕</button>}
    </div>
  );
}
